import React, { useState, useEffect, useCallback } from 'react';
import {
  View, Text, ActivityIndicator, ScrollView, StyleSheet,
  TouchableOpacity, useWindowDimensions,
} from 'react-native';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { db } from '../firebase/firebase';
import { getTotalCounts, getMonthlyStats, getLifetimeAmounts, TotalCounts, MonthlyStats, LifetimeAmounts } from '../query/analyticsService';

interface Props { branchId?: string; }

const C = {
  primary: '#21808D', primaryDark: '#1a6672', primaryLight: '#e8f5f7',
  success: '#10b981', successLight: '#d1fae5',
  danger: '#ef4444', dangerLight: '#fee2e2',
  warning: '#f59e0b', warningLight: '#fef3c7',
  purple: '#8b5cf6', purpleLight: '#ede9fe',
  blue: '#3b82f6', blueLight: '#dbeafe',
  text: '#1a2332', muted: '#64748b', border: '#e2e8f0', bg: '#f6f8fb',
};

const MONTH_NAMES = ['January','February','March','April','May','June',
  'July','August','September','October','November','December'];

const PIE_COLORS = ['#21808D','#10b981','#3b82f6','#f59e0b','#8b5cf6','#ef4444'];

export default function AnalyticsDashboard({ branchId = '' }: Props) {
  const { width } = useWindowDimensions();
  const isMobile = width < 700;

  const [counts, setCounts] = useState<TotalCounts | null>(null);
  const [stats, setStats] = useState<MonthlyStats | null>(null);
  const [lifetime, setLifetime] = useState<LifetimeAmounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'monthly'>('overview');
  const [currentMonth, setCurrentMonth] = useState(() => {
    const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const start = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1, 0, 0, 0);
      const end = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0, 23, 59, 59);
      const [c, s, l] = await Promise.all([
        getTotalCounts(db),
        getMonthlyStats(db, start, end, branchId),
        getLifetimeAmounts(db, branchId || undefined),
      ]);
      setCounts(c); setStats(s); setLifetime(l);
    } catch (e: any) { setError(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, [currentMonth, branchId]);

  useEffect(() => { load(); }, [load]);

  const prevMonth = () => setCurrentMonth(m => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  const nextMonth = () => setCurrentMonth(m => new Date(m.getFullYear(), m.getMonth() + 1, 1));
  const isCurrentMonth = currentMonth.getMonth() === new Date().getMonth() &&
    currentMonth.getFullYear() === new Date().getFullYear();

  const totalRevenue = stats
    ? stats.breakdown.totalPrice + stats.breakdown.deliveryCharge +
      stats.breakdown.packing + stats.breakdown.service + stats.breakdown.tax
    : 0;

  const pieData = stats ? [
    { name: 'Product Price',   value: stats.breakdown.totalPrice,     color: '#21808D' },
    { name: 'Delivery',        value: stats.breakdown.deliveryCharge, color: '#3b82f6' },
    { name: 'Packing',         value: stats.breakdown.packing,        color: '#f59e0b' },
    { name: 'Service',         value: stats.breakdown.service,        color: '#8b5cf6' },
    { name: 'Tax',             value: stats.breakdown.tax,            color: '#64748b' },
    { name: 'Commission (10%)',value: stats.breakdown.commission,     color: '#ef4444' },
  ].filter(d => d.value > 0) : [];

  const orderPieData = stats ? [
    { name: 'Delivered', value: stats.deliveredCount, color: '#10b981' },
    { name: 'Cancelled', value: stats.cancelledCount, color: '#ef4444' },
    { name: 'Pending',   value: stats.pendingCount,   color: '#f59e0b' },
  ].filter(d => d.value > 0) : [];

  // ── KPI Card ──────────────────────────────────────────────────────
  const KpiCard = ({ label, value, sub, icon, color, bg }: {
    label: string; value: string | number; sub?: string;
    icon: string; color: string; bg: string;
  }) => (
    <View style={[st.kpiCard, { borderLeftColor: color, borderLeftWidth: 4 }]}>
      <View style={[st.kpiIconBox, { backgroundColor: bg }]}>
        <Text style={st.kpiIcon}>{icon}</Text>
      </View>
      <View style={{ flex: 1 }}>
        <Text style={st.kpiLabel}>{label}</Text>
        <Text style={[st.kpiValue, { color }]}>{value}</Text>
        {sub ? <Text style={st.kpiSub}>{sub}</Text> : null}
      </View>
    </View>
  );

  // ── Revenue bar ───────────────────────────────────────────────────
  const RevBar = ({ label, value, total, color }: { label: string; value: number; total: number; color: string }) => {
    const pct = total > 0 ? (value / total) * 100 : 0;
    return (
      <View style={st.revRow}>
        <Text style={st.revLabel}>{label}</Text>
        <View style={st.revBarTrack}>
          <View style={[st.revBarFill, { width: `${pct}%` as any, backgroundColor: color }]} />
        </View>
        <Text style={[st.revValue, { color }]}>₹{value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</Text>
        <Text style={st.revPct}>{pct.toFixed(1)}%</Text>
      </View>
    );
  };

  const cols = isMobile ? 1 : 2;

  return (
    <ScrollView style={st.container} contentContainerStyle={{ paddingBottom: 40 }}>
      {/* ── Tabs ── */}
      <View style={st.tabs}>
        {(['overview', 'monthly'] as const).map(tab => (
          <TouchableOpacity
            key={tab} style={[st.tab, activeTab === tab && st.tabActive]}
            onPress={() => setActiveTab(tab)}
          >
            <Text style={[st.tabTxt, activeTab === tab && st.tabTxtActive]}>
              {tab === 'overview' ? '📊 Overview' : '📅 Monthly'}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {loading && (
        <View style={st.loadBox}>
          <ActivityIndicator size="large" color={C.primary} />
          <Text style={st.loadTxt}>Loading analytics…</Text>
        </View>
      )}

      {error && !loading && (
        <View style={st.errorBox}>
          <Text style={st.errorTxt}>⚠️  {error}</Text>
          <TouchableOpacity style={st.retryBtn} onPress={load}>
            <Text style={st.retryTxt}>Retry</Text>
          </TouchableOpacity>
        </View>
      )}

      {!loading && !error && (
        <>
          {/* ══════════════════ OVERVIEW TAB ══════════════════ */}
          {activeTab === 'overview' && counts && (
            <>
              <View style={st.section}>
                <Text style={st.sectionTitle}>Platform Overview</Text>
                <Text style={st.sectionSub}>All-time counts across your platform</Text>

                <View style={st.kpiGrid}>
                  <KpiCard label="Total Orders"      value={counts.totalOrders.toLocaleString()}           icon="📦" color={C.primary}  bg={C.primaryLight}  />
                  <KpiCard label="Registered Users"  value={counts.totalUsers.toLocaleString()}            icon="👤" color={C.blue}     bg={C.blueLight}     />
                  <KpiCard label="Restaurants"       value={counts.totalRestaurants.toLocaleString()}      icon="🍽️" color={C.warning}  bg={C.warningLight}  />
                  <KpiCard label="Delivery Partners" value={counts.totalDeliveryPartners.toLocaleString()} icon="🛵" color={C.purple}  bg={C.purpleLight}   />
                  <KpiCard label="Branches"          value={counts.totalBranches.toLocaleString()}         icon="🏢" color="#0f766e"   bg="#ccfbf1"          />
                  <KpiCard label="Products"          value={counts.totalProducts.toLocaleString()}         icon="🛍️" color={C.danger}  bg={C.dangerLight}   />
                  <KpiCard label="Sellers"           value={counts.totalSellers.toLocaleString()}          icon="🏪" color="#0369a1"   bg="#e0f2fe"          />
                  <KpiCard label="Categories"        value={counts.totalCategories.toLocaleString()}       icon="🗂️" color="#7c3aed"   bg="#ede9fe"          />
                </View>
              </View>

              {/* Lifetime Revenue Section */}
              {lifetime && (
                <View style={st.section}>
                  <Text style={st.sectionTitle}>Lifetime Revenue</Text>
                  <Text style={st.sectionSub}>Cumulative amounts across all orders ever placed</Text>

                  {/* All Orders */}
                  <Text style={st.lifetimeGroupLabel}>📦 All Orders</Text>
                  <View style={st.kpiGrid}>
                    <KpiCard label="Total Orders"          value={lifetime.totalOrderCount.toLocaleString('en-IN')}                                                                icon="📦" color={C.primary}  bg={C.primaryLight} sub="All time order count" />
                    <KpiCard label="Delivered Orders"      value={lifetime.deliveredOrderCount.toLocaleString('en-IN')}                                                            icon="✅" color={C.success}  bg={C.successLight} sub={`${lifetime.totalOrderCount > 0 ? ((lifetime.deliveredOrderCount / lifetime.totalOrderCount) * 100).toFixed(1) : 0}% of total`} />
                    <KpiCard label="Total Product Amount"  value={`₹${lifetime.totalOrderProductAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}                    icon="💰" color="#0369a1"    bg="#e0f2fe"         sub="Sum of all order product prices" />
                    <KpiCard label="All Orders Revenue"    value={`₹${lifetime.allOrdersTotalRevenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}                      icon="📊" color={C.purple}   bg={C.purpleLight}  sub="Product + Delivery + Packing + Tax + Svc" />
                  </View>

                  {/* Delivered Orders */}
                  <Text style={st.lifetimeGroupLabel}>✅ Delivered Orders Only</Text>
                  <View style={st.kpiGrid}>
                    <KpiCard label="Product Amount"        value={`₹${lifetime.deliveredProductAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}                    icon="🛍️" color={C.success}  bg={C.successLight} sub="Delivered orders product price" />
                    <KpiCard label="Delivery Charge"       value={`₹${lifetime.deliveredDeliveryCharge.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}                   icon="🛵" color={C.blue}      bg={C.blueLight}    sub="Delivered orders delivery charge" />
                    <KpiCard label="Packing Amount"        value={`₹${lifetime.deliveredPacking.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}                          icon="📦" color={C.warning}   bg={C.warningLight} sub="Delivered orders packing charge" />
                    <KpiCard label="Delivered Revenue"     value={`₹${lifetime.deliveredTotalRevenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}                     icon="✅" color={C.success}   bg={C.successLight} sub="Product + Delivery + Packing + Tax + Svc" />
                  </View>

                  {/* Averages */}
                  <Text style={st.lifetimeGroupLabel}>📈 Averages</Text>
                  <View style={st.kpiGrid}>
                    <KpiCard label="Avg Order Value"           value={`₹${lifetime.avgOrderValue.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`}           icon="📉" color={C.purple}  bg={C.purpleLight}  sub={`Across all ${lifetime.totalOrderCount} orders`} />
                    <KpiCard label="Avg Delivered Order Value" value={`₹${lifetime.avgDeliveredOrderValue.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`}  icon="📈" color={C.success} bg={C.successLight} sub={`Across ${lifetime.deliveredOrderCount} delivered orders`} />
                    <KpiCard label="Avg Delivery Charge"       value={`₹${lifetime.avgDeliveryCharge.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`}       icon="🛵" color={C.blue}    bg={C.blueLight}    sub="Per delivered order" />
                  </View>
                </View>
              )}
            </>
          )}

          {/* ══════════════════ MONTHLY TAB ══════════════════ */}
          {activeTab === 'monthly' && stats && (
            <>
              {/* Month picker */}
              <View style={st.monthPicker}>
                <TouchableOpacity style={st.monthBtn} onPress={prevMonth}>
                  <Text style={st.monthBtnTxt}>‹</Text>
                </TouchableOpacity>
                <View style={st.monthCenter}>
                  <Text style={st.monthName}>
                    {MONTH_NAMES[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                  </Text>
                  {isCurrentMonth && <View style={st.currentMonthTag}><Text style={st.currentMonthTxt}>Current</Text></View>}
                </View>
                <TouchableOpacity style={st.monthBtn} onPress={nextMonth}>
                  <Text style={st.monthBtnTxt}>›</Text>
                </TouchableOpacity>
              </View>

              {/* Revenue hero */}
              <View style={st.revenueHero}>
                <View>
                  <Text style={st.revenueHeroLabel}>Total Revenue</Text>
                  <Text style={st.revenueHeroValue}>
                    ₹{totalRevenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </Text>
                  <Text style={st.revenueHeroSub}>From {stats.deliveredCount} delivered orders</Text>
                </View>
                <View style={st.revenueStats}>
                  <View style={st.revStat}>
                    <Text style={st.revStatVal}>₹{stats.netRevenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</Text>
                    <Text style={st.revStatLabel}>Net Revenue</Text>
                  </View>
                  <View style={[st.revStat, { borderLeftWidth: 1, borderLeftColor: 'rgba(255,255,255,0.3)', paddingLeft: 16 }]}>
                    <Text style={st.revStatVal}>₹{stats.avgOrderValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</Text>
                    <Text style={st.revStatLabel}>Avg Order</Text>
                  </View>
                </View>
              </View>

              {/* Order status cards */}
              <View style={st.section}>
                <Text style={st.sectionTitle}>Order Status — {MONTH_NAMES[currentMonth.getMonth()]}</Text>
                <View style={st.kpiGrid}>
                  <KpiCard label="Delivered"            value={stats.deliveredCount.toLocaleString()} sub={`${stats.totalCount > 0 ? ((stats.deliveredCount / stats.totalCount) * 100).toFixed(1) : 0}% of total`} icon="✅" color={C.success} bg={C.successLight} />
                  <KpiCard label="Cancelled"            value={stats.cancelledCount.toLocaleString()} sub={`${stats.totalCount > 0 ? ((stats.cancelledCount / stats.totalCount) * 100).toFixed(1) : 0}% of total`} icon="❌" color={C.danger}  bg={C.dangerLight}  />
                  <KpiCard label="Pending / Processing" value={stats.pendingCount.toLocaleString()}   sub={`${stats.totalCount > 0 ? ((stats.pendingCount  / stats.totalCount) * 100).toFixed(1) : 0}% of total`} icon="⏳" color={C.warning} bg={C.warningLight} />
                  <KpiCard label="Total Orders (Month)" value={stats.totalCount.toLocaleString()}                                                                                                                    icon="📦" color={C.primary} bg={C.primaryLight} />
                </View>

                {/* Order status pie */}
                {orderPieData.length > 0 && (
                  <View style={st.chartCard}>
                    <Text style={st.chartTitle}>Order Status Distribution</Text>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={orderPieData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value">
                          {orderPieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                        </Pie>
                        <Tooltip formatter={(v: number) => [v.toLocaleString(), '']} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </View>
                )}
              </View>

              {/* Revenue breakdown */}
              <View style={st.section}>
                <Text style={st.sectionTitle}>Revenue Breakdown</Text>
                <Text style={st.sectionSub}>Delivered orders only</Text>

                <View style={st.breakdownCards}>
                  {[
                    { label: 'Product Price',    value: stats.breakdown.totalPrice,     color: '#21808D' },
                    { label: 'Delivery Charges', value: stats.breakdown.deliveryCharge, color: '#3b82f6' },
                    { label: 'Packing',          value: stats.breakdown.packing,        color: '#f59e0b' },
                    { label: 'Service Fee',      value: stats.breakdown.service,        color: '#8b5cf6' },
                    { label: 'Tax',              value: stats.breakdown.tax,            color: '#64748b' },
                    { label: 'Commission (10%)', value: stats.breakdown.commission,     color: '#ef4444' },
                  ].map(({ label, value, color }) => (
                    <View key={label} style={[st.breakdownCard, { borderTopColor: color }]}>
                      <View style={[st.breakdownDot, { backgroundColor: color }]} />
                      <Text style={st.breakdownLabel}>{label}</Text>
                      <Text style={[st.breakdownValue, { color }]}>
                        ₹{value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </Text>
                      <Text style={st.breakdownPct}>
                        {totalRevenue > 0 ? ((value / totalRevenue) * 100).toFixed(1) : 0}%
                      </Text>
                    </View>
                  ))}
                </View>

                {/* Revenue progress bars */}
                <View style={st.revBars}>
                  {[
                    { label: 'Product Price',    value: stats.breakdown.totalPrice,     color: '#21808D' },
                    { label: 'Delivery',         value: stats.breakdown.deliveryCharge, color: '#3b82f6' },
                    { label: 'Commission',        value: stats.breakdown.commission,     color: '#ef4444' },
                    { label: 'Packing',          value: stats.breakdown.packing,        color: '#f59e0b' },
                    { label: 'Service',          value: stats.breakdown.service,        color: '#8b5cf6' },
                    { label: 'Tax',              value: stats.breakdown.tax,            color: '#64748b' },
                  ].map(item => (
                    <RevBar key={item.label} {...item} total={totalRevenue} />
                  ))}
                </View>

                {/* Revenue pie */}
                {pieData.length > 0 && (
                  <View style={st.chartCard}>
                    <Text style={st.chartTitle}>Revenue Distribution</Text>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie data={pieData} cx="50%" cy="50%" outerRadius={110} paddingAngle={2} dataKey="value"
                          label={({ name, percent }: any) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          labelLine={false}
                        >
                          {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                        </Pie>
                        <Tooltip formatter={(v: number) => [`₹${v.toFixed(2)}`, '']} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </View>
                )}
              </View>

              {/* Daily bar chart */}
              {stats.dailyData.length > 0 && (
                <View style={st.section}>
                  <Text style={st.sectionTitle}>Daily Orders — {MONTH_NAMES[currentMonth.getMonth()]}</Text>
                  <Text style={st.sectionSub}>Delivered vs Cancelled per day</Text>
                  <View style={st.chartCard}>
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={stats.dailyData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#64748b' }} />
                        <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                        <Tooltip
                          contentStyle={{ borderRadius: 10, fontSize: 13 }}
                          formatter={(v: number, name: string) => [v, name]}
                        />
                        <Bar dataKey="delivered" name="Delivered" fill="#10b981" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="cancelled"  name="Cancelled"  fill="#ef4444" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="pending"   name="Pending"   fill="#f59e0b" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </View>

                  <View style={st.chartCard}>
                    <Text style={[st.chartTitle, { marginBottom: 12 }]}>Daily Revenue (₹)</Text>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={stats.dailyData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#64748b' }} />
                        <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
                        <Tooltip contentStyle={{ borderRadius: 10, fontSize: 13 }} formatter={(v: number) => [`₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, 'Revenue']} />
                        <Bar dataKey="revenue" name="Revenue" fill="#21808D" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </View>
                </View>
              )}

              {stats.totalCount === 0 && (
                <View style={st.emptyBox}>
                  <Text style={{ fontSize: 48 }}>📭</Text>
                  <Text style={st.emptyTitle}>No orders found</Text>
                  <Text style={st.emptySub}>No data for {MONTH_NAMES[currentMonth.getMonth()]} {currentMonth.getFullYear()}</Text>
                </View>
              )}
            </>
          )}
        </>
      )}
    </ScrollView>
  );
}

const st = StyleSheet.create({
  container: { flex: 1, backgroundColor: C.bg },

  tabs: { flexDirection: 'row', backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: C.border },
  tab: { flex: 1, paddingVertical: 14, alignItems: 'center' },
  tabActive: { borderBottomWidth: 3, borderBottomColor: C.primary },
  tabTxt: { fontSize: 14, fontWeight: '600', color: C.muted },
  tabTxtActive: { color: C.primary },

  loadBox: { alignItems: 'center', paddingVertical: 48 },
  loadTxt: { marginTop: 12, color: C.muted, fontSize: 14 },

  errorBox: { margin: 20, backgroundColor: '#fff', borderRadius: 12, padding: 24, alignItems: 'center', borderLeftWidth: 4, borderLeftColor: C.danger },
  errorTxt: { color: C.danger, fontSize: 14, marginBottom: 12, textAlign: 'center' },
  retryBtn: { backgroundColor: C.primary, paddingHorizontal: 20, paddingVertical: 10, borderRadius: 8 },
  retryTxt: { color: '#fff', fontWeight: '600', fontSize: 13 },

  section: { backgroundColor: '#fff', margin: 12, borderRadius: 16, padding: 18, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.06, shadowRadius: 8, elevation: 2 },
  sectionTitle: { fontSize: 17, fontWeight: '700', color: C.text, marginBottom: 4 },
  sectionSub: { fontSize: 12, color: C.muted, marginBottom: 14 },
  lifetimeGroupLabel: { fontSize: 12, fontWeight: '700', color: C.text, marginTop: 14, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 },

  kpiGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginBottom: 12 },
  kpiCard: {
    flex: 1, flexBasis: '45%', minHeight: 80, backgroundColor: '#fff', borderRadius: 14, padding: 16,
    flexDirection: 'row', alignItems: 'center', gap: 14,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4, elevation: 1,
    borderWidth: 1, borderColor: C.border,
  },
  kpiIconBox: { width: 46, height: 46, borderRadius: 12, justifyContent: 'center', alignItems: 'center' },
  kpiIcon: { fontSize: 20 },
  kpiLabel: { fontSize: 12, color: C.muted, fontWeight: '500', marginBottom: 4 },
  kpiValue: { fontSize: 24, fontWeight: '800', color: C.text },
  kpiSub: { fontSize: 11, color: C.muted, marginTop: 3 },

  monthPicker: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', margin: 12, marginBottom: 0, borderRadius: 14, padding: 12, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4, elevation: 1 },
  monthBtn: { width: 40, height: 40, borderRadius: 10, backgroundColor: C.primaryLight, justifyContent: 'center', alignItems: 'center' },
  monthBtnTxt: { fontSize: 22, color: C.primary, fontWeight: '700', lineHeight: 28 },
  monthCenter: { flex: 1, alignItems: 'center', flexDirection: 'row', justifyContent: 'center', gap: 8 },
  monthName: { fontSize: 17, fontWeight: '700', color: C.text },
  currentMonthTag: { backgroundColor: C.successLight, borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3 },
  currentMonthTxt: { fontSize: 10, fontWeight: '700', color: C.success },

  revenueHero: {
    margin: 12, marginBottom: 0, borderRadius: 16, padding: 20,
    backgroundColor: C.primaryDark,
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end',
  },
  revenueHeroLabel: { fontSize: 12, color: 'rgba(255,255,255,0.7)', fontWeight: '600', letterSpacing: 1, textTransform: 'uppercase' as const, marginBottom: 6 },
  revenueHeroValue: { fontSize: 34, fontWeight: '800', color: '#fff', letterSpacing: -1 },
  revenueHeroSub: { fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 4 },
  revenueStats: { flexDirection: 'row', gap: 0, alignItems: 'flex-end' },
  revStat: { alignItems: 'flex-end', paddingLeft: 16 },
  revStatVal: { fontSize: 16, fontWeight: '700', color: '#fff' },
  revStatLabel: { fontSize: 10, color: 'rgba(255,255,255,0.6)', marginTop: 2 },

  breakdownCards: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 16 },
  breakdownCard: {
    backgroundColor: '#f8fafc', borderRadius: 12, padding: 14,
    minWidth: 130, flexBasis: '30%', flexGrow: 1,
    borderTopWidth: 3, borderWidth: 1, borderColor: C.border,
  },
  breakdownDot: { width: 8, height: 8, borderRadius: 4, marginBottom: 8 },
  breakdownLabel: { fontSize: 11, color: C.muted, fontWeight: '500', marginBottom: 6 },
  breakdownValue: { fontSize: 18, fontWeight: '700', marginBottom: 2 },
  breakdownPct: { fontSize: 11, color: C.muted },

  revBars: { marginBottom: 4 },
  revRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 10 },
  revLabel: { fontSize: 12, color: C.muted, width: 90, fontWeight: '500' },
  revBarTrack: { flex: 1, height: 8, backgroundColor: '#f1f5f9', borderRadius: 4, overflow: 'hidden' },
  revBarFill: { height: 8, borderRadius: 4 },
  revValue: { fontSize: 12, fontWeight: '700', width: 70, textAlign: 'right' as const },
  revPct: { fontSize: 11, color: C.muted, width: 36, textAlign: 'right' as const },

  chartCard: { backgroundColor: '#f8fafc', borderRadius: 12, padding: 14, marginTop: 12 },
  chartTitle: { fontSize: 14, fontWeight: '700', color: C.text, marginBottom: 4, textAlign: 'center' as const },

  emptyBox: { alignItems: 'center', paddingVertical: 48 },
  emptyTitle: { fontSize: 18, fontWeight: '700', color: C.text, marginTop: 12 },
  emptySub: { fontSize: 13, color: C.muted, marginTop: 4 },
});
